import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Link } from 'react-router-dom';
import { ElementType, FC, JSX, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Roles } from '@callmart/types';

interface MenuSimpleItem {
    text: string;
    value: string;
    icon?: ElementType;
    role?: Roles | '';
    roles?: readonly Roles[];
    permissions?: string[];
}

export interface MenuItemParent extends MenuSimpleItem {
    nestedItems: MenuItemNode[];
}

export interface MenuItemChild extends MenuSimpleItem {
    path: string;
}

export type MenuItemNode = MenuItemParent | MenuItemChild;

interface MenuItemProps {
    item: MenuItemNode;
    isNestedItem?: boolean;
    nestedItems?: MenuItemNode[];
    isMenuOpen: boolean;
    showMenuItemExpandIcon: (item: MenuItemNode) => JSX.Element | null;
    onClick?: (event: MouseEvent) => void;
    /**
     * Path of the menu item the current route resolved to (longest prefix
     * match, computed once in SideDrawer via findMenuItem). Selection compares
     * against this instead of the raw location so detail routes highlight
     * their closest entry and `/budget` never shadows deeper budget pages.
     */
    selectedPath?: string;
}

const MenuItem: FC<MenuItemProps> = ({
    item,
    isNestedItem,
    nestedItems,
    isMenuOpen,
    showMenuItemExpandIcon,
    onClick,
    selectedPath,
    ...props
}) => {
    const { t } = useTranslation('navigation');
    const isSelected = 'path' in item && Boolean(item.path) && item.path === selectedPath;
    const isNestedItemSelected = nestedItems?.some(
        (nestedItem) => 'path' in nestedItem && Boolean(nestedItem.path) && nestedItem.path === selectedPath
    );

    return (
        <ListItemButton
            onClick={onClick}
            component={'nestedItems' in item && item.nestedItems.length ? 'div' : Link}
            selected={isSelected || isNestedItemSelected}
            {...('path' in item && item.path ? { to: item.path } : {})}
            {...props}
        >
            <ListItemIcon>
                {((isMenuOpen && !isNestedItem) || !isMenuOpen) && item.icon ? <item.icon /> : null}
            </ListItemIcon>
            <ListItemText
                primary={item.value ? t(item.value) : item.text}
                primaryTypographyProps={isMenuOpen ? { whiteSpace: 'normal' } : { whiteSpace: 'nowrap' }}
            />
            {showMenuItemExpandIcon(item)}
        </ListItemButton>
    );
};

export default MenuItem;
